import type { AxiosError } from 'axios';
import type { ErroApi, ErroDetalhe } from '@/types/api';

export function textoLimiteCadastro(
  recurso: 'ESCOLA' | 'ALUNO',
  limite: number,
  emUso: number,
): string {
  if (recurso === 'ALUNO') {
    return `Limite de alunos atingido. O plano libera ${limite} e já existem ${emUso} ativos.`;
  }
  return `Limite de escolas atingido. O plano libera ${limite} e já existem ${emUso} ativas.`;
}

function mensagemLimiteCadastro(erro: ErroApi): string {
  const detalhes = erro.detalhes;
  if (detalhes && typeof detalhes === 'object' && !Array.isArray(detalhes)) {
    const dados = detalhes as { recurso?: unknown; limite?: unknown; emUso?: unknown };
    const limite = typeof dados.limite === 'number' ? dados.limite : null;
    const emUso = typeof dados.emUso === 'number' ? dados.emUso : null;
    if (limite != null && emUso != null) {
      if (dados.recurso === 'ALUNO') {
        return textoLimiteCadastro('ALUNO', limite, emUso);
      }
      return textoLimiteCadastro('ESCOLA', limite, emUso);
    }
  }
  return 'Limite de cadastro atingido.';
}

const MENSAGENS: Record<string, string> = {
  CREDENCIAIS_INVALIDAS: 'Login ou senha incorretos.',
  TOKEN_AUSENTE: 'Sessao expirada. Faca login novamente.',
  TOKEN_INVALIDO: 'Sessao invalida. Faca login novamente.',
  TOKEN_EXPIRADO: 'Sessao expirada. Faca login novamente.',
  USUARIO_INATIVO: 'Conta inativa. Entre em contato com a escola.',
  ESCOLA_NAO_SELECIONADA: 'Selecione a escola ativa para continuar.',
  SEM_PERMISSAO: 'Voce nao tem permissao para esta acao.',
  DADOS_INVALIDOS: 'Verifique os dados informados.',
  METODO_NAO_PERMITIDO: 'Operacao nao permitida.',
  ROTA_NAO_ENCONTRADA: 'Recurso nao encontrado.',
  TIPO_CONTEUDO_NAO_SUPORTADO: 'Formato de requisicao invalido.',
  ARQUIVO_MUITO_GRANDE: 'Arquivo grande demais (maximo 10 MB para anexos).',
  ARQUIVO_TIPO_NAO_SUPORTADO: 'Envie uma imagem JPEG, PNG, WebP ou GIF, ou um PDF.',
  ARQUIVO_FALHA_UPLOAD: 'Falha ao enviar o arquivo. Tente novamente.',
  CEP_NAO_ENCONTRADO: 'CEP nao encontrado.',
  INTEGRACAO_INDISPONIVEL: 'Servico externo indisponivel. Tente novamente.',
  ERRO_INTERNO: 'Erro interno. Tente novamente.',
};

export function extractErroApi(error: unknown): ErroApi | null {
  const axiosError = error as AxiosError<ErroApi>;
  if (axiosError?.response?.data?.codigo) {
    return axiosError.response.data;
  }
  return null;
}

export function getErrorMessage(error: unknown, fallback = 'Ocorreu um erro.'): string {
  const erro = extractErroApi(error);
  if (erro?.codigo === 'ACESSO_SEQUENCIA_INVALIDA' && erro.mensagem) {
    return erro.mensagem;
  }
  if (erro?.codigo === 'LIMITE_CADASTRO_ATINGIDO') {
    return mensagemLimiteCadastro(erro);
  }
  // DADOS_INVALIDOS costuma trazer mensagem específica do domínio (ex.: horário futuro).
  if (erro?.codigo === 'DADOS_INVALIDOS' && erro.mensagem) {
    const genericas = new Set([
      'Payload nao passou na validacao.',
      'JSON invalido ou tipos incompativeis com o contrato.',
      'Parametro com tipo invalido.',
      'Parametro obrigatorio ausente.',
      'Upload invalido.',
    ]);
    if (!genericas.has(erro.mensagem)) {
      return erro.mensagem;
    }
  }
  if (erro?.codigo && MENSAGENS[erro.codigo]) {
    return MENSAGENS[erro.codigo];
  }
  if (erro?.mensagem) {
    return erro.mensagem;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

export function getErrorCode(error: unknown): string | null {
  return extractErroApi(error)?.codigo ?? null;
}

export function mensagemDoCampo(error: unknown, campo: string): string | null {
  const erro = extractErroApi(error);
  if (!erro || !Array.isArray(erro.detalhes)) {
    return null;
  }
  const detalhe = (erro.detalhes as ErroDetalhe[]).find((item) => item.campo === campo);
  if (!detalhe) {
    return null;
  }
  return detalhe.mensagem?.trim() || getErrorMessage(error);
}

export function isErroCode(error: unknown, codigo: string): boolean {
  return getErrorCode(error) === codigo;
}
